import React, { useState } from 'react';
import {
  Box,
  Button,
  Popover,
  TextField,
  ClickAwayListener,
} from '@mui/material';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  allowTransparent?: boolean;
}

const PRESET_COLORS = [
  '#000000',
  '#ffffff',
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  '#ff5722',
  '#795548',
  '#607d8b',
];

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  allowTransparent = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState(color);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setInputValue(color);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (newColor: string) => {
    onChange(newColor);
    setInputValue(newColor);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    // Validate hex color
    if (/^#([0-9A-F]{3}){1,2}$/i.test(inputValue)) {
      onChange(inputValue);
    } else {
      setInputValue(color);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          onClick={handleClick}
          sx={{
            minWidth: 36,
            width: 36,
            height: 36,
            padding: 0,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: color === 'transparent' ? 'transparent' : color,
            backgroundImage:
              color === 'transparent'
                ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                : 'none',
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 4px 4px',
            '&:hover': {
              backgroundColor: color === 'transparent' ? 'transparent' : color,
            },
          }}
        />
        <TextField
          size="small"
          value={color === 'transparent' ? 'transparent' : color}
          onClick={handleClick}
          sx={{ flexGrow: 1 }}
          InputProps={{
            readOnly: true,
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={{ width: 220, p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Hex Color"
                size="small"
                fullWidth
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                placeholder="#000000"
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 0.5,
                mb: allowTransparent ? 1 : 0,
              }}
            >
              {PRESET_COLORS.map((presetColor) => (
                <Button
                  key={presetColor}
                  onClick={() => handleColorSelect(presetColor)}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    p: 0,
                    backgroundColor: presetColor,
                    border:
                      color === presetColor
                        ? '2px solid black'
                        : '1px solid #ddd',
                    '&:hover': {
                      backgroundColor: presetColor,
                      opacity: 0.8,
                    },
                  }}
                />
              ))}
            </Box>

            {allowTransparent && (
              <Button
                onClick={() => handleColorSelect('transparent')}
                fullWidth
                variant="outlined"
                sx={{
                  mt: 1,
                  backgroundImage:
                    'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 4px 4px',
                  border:
                    color === 'transparent'
                      ? '2px solid black'
                      : '1px solid #ddd',
                }}
              >
                Transparent
              </Button>
            )}
          </Box>
        </ClickAwayListener>
      </Popover>
    </>
  );
};

export default ColorPicker;
